import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import FeatureCard from './FeatureCard';
import { useFeatures } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import LoadingSpinner from '../ui/LoadingSpinner';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import '../../styles/components/masonry-grid.css';

const CategoryFeatures: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const { data: allFeatures, loading: featuresLoading } = useFeatures();

  // Filter features by category
  const categoryFeatures = useMemo(() => {
    if (!allFeatures || !category) return [];
    
    const filtered = allFeatures.filter(feature => 
      feature.category?.toLowerCase() === category.toLowerCase() && 
      feature.is_published && 
      feature.public_visibility
    );
    
    return filtered;
  }, [allFeatures, category]);

  // Create breadcrumbs
  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Home', path: '/' },
      { label: 'Features', path: '/features' },
    ];
    
    if (category) {
      const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
      items.push({ label: categoryLabel, isActive: true });
    } else {
      items.push({ label: 'Category Not Found', isActive: true });
    }
    
    return items;
  }, [category]);

  // Set canonical URL
  useCanonicalUrl({
    title: category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Features | LUT Rolls` : 'Feature Category | LUT Rolls',
    description: category ? `Browse all features in the ${category} category.` : 'Browse features by category.',
    path: `/features/${category || 'category'}`
  });

  // Mobile detection for layout switching
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to distribute items across columns for grid layout
  const distributeItemsInColumns = useMemo(() => {
    if (isMobile || categoryFeatures.length === 0) return [[], []];
    
    const column1: typeof categoryFeatures = [];
    const column2: typeof categoryFeatures = [];
    
    // Distribute items alternately between columns
    categoryFeatures.forEach((feature, index) => {
      if (index % 2 === 0) {
        column1.push(feature);
      } else {
        column2.push(feature);
      }
    });
    
    return [column1, column2];
  }, [categoryFeatures, isMobile]);

  // Show loading when we don't have any features data yet
  if (featuresLoading && !allFeatures) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading features..." />
        </main>
      </div>
    );
  }

  // Show category not found if category param is missing
  if (!category) {
    return (
      <div className="page-container">
        <main className="page-main">
          <div className="page-content">
            <Breadcrumbs items={breadcrumbs} />
            <header className="page-header">
              <h1>Feature Category Not Found</h1>
              <div className="page-meta">
                <p>The feature category you're looking for doesn't exist.</p>
              </div>
            </header>
          </div>
        </main>
      </div>
    );
  }

  const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <div className="page-container">
      <main className="page-main">
        <div className="page-content">
          <Breadcrumbs items={breadcrumbs} />
          
          <header className="page-header">
            <h1>{categoryLabel} Features</h1>
            <div className="page-meta">
              <p>
                {categoryFeatures.length > 0 
                  ? `${categoryFeatures.length} feature${categoryFeatures.length === 1 ? '' : 's'} in the ${categoryLabel} category`
                  : `No features found in the ${categoryLabel} category`
                }
              </p>
            </div>
          </header>

          <div className="features-content">
            {featuresLoading && (
              <div className="loading-card">
                <div className="card-content">
                  <p>Loading features...</p>
                </div>
              </div>
            )}

            {!featuresLoading && categoryFeatures.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="card-content">
                  <h3>No Features Available</h3>
                  <p>No published features found in the {categoryLabel} category.</p>
                  <p style={{ fontSize: '0.9em', opacity: 0.7 }}>
                    Features will appear here once they are added to this category.
                  </p>
                </div>
              </div>
            )}

            {categoryFeatures.length > 0 && (
              <>
                {isMobile ? (
                  // Mobile: Use simple vertical layout
                  <div className="mobile-cards-container">
                    {categoryFeatures.map((feature) => (
                      <div key={feature.id} className="mobile-card-wrapper">
                        <FeatureCard feature={feature} fromCategory={true} />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop/Tablet: CSS masonry layout
                  <div className="css-masonry-container">
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[0].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} fromCategory={true} />
                        </div>
                      ))}
                    </div>
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[1].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} fromCategory={true} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CategoryFeatures;